import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Grid,
  Typography,
  Box,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

export default function SummaryTable({ data }) {
  const { t } = useTranslation();

  /* ----------------------------------------------------------
   * 1 · Helper to build one set of rows (shift OR day)
   * ---------------------------------------------------------- */
  const providers = [
    { key: 'ch', label: 'CH' },
    { key: 'ok', label: 'OK D.' },
    { key: 'wg', label: 'WG' },
    { key: 'nt', label: 'NT' },
    { key: 'kiosk', label: 'Kiosk' },
  ];

  /** Returns an array of row objects {label, inVal, outVal, net}. */
  function makeRows(src = {}) {
    const rows = [];

    // Provider rows
    providers.forEach(({ key, label }) => {
      const inVal = Number(src[`${key}CashIn`] ?? 0);
      const outVal = Number(src[`${key}CashOut`] ?? 0);
      const netVal = Number(src[`${key}Neto`] ?? inVal - outVal);
      rows.push({ label, inVal, outVal, netVal });
    });

    // Payment Cash (has In/Out)
    const pcIn = Number(src.paymentCashIn ?? 0);
    const pcOut = Number(src.paymentCashOut ?? 0);
    rows.push({
      label: t('avslutteSkiftet.paymentCash'),
      inVal: pcIn,
      outVal: pcOut,
      netVal: pcIn - pcOut,
    });

    // Payment Card (single net)
    rows.push({
      label: t('avslutteSkiftet.paymentCard'),
      inVal: '',
      outVal: '',
      netVal: Number(src.paymentCard ?? 0),
    });

    // Payment Account (single net)
    rows.push({
      label: t('avslutteSkiftet.paymentAccount'),
      inVal: '',
      outVal: '',
      netVal: Number(src.paymentAccount ?? 0),
    });

    return rows;
  }

  const shiftRows = makeRows(data); // current shift
  const dayRows = makeRows(data.totalsDay || {}); // whole day

  /* ----------------------------------------------------------
   * 2 · Helper to render a single table
   * ---------------------------------------------------------- */
  const renderTable = (rows, title) => (
    <Grid item xs={12} md={6}>
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="h6"
          sx={{
            color: 'white',
            textAlign: 'center',
            mb: 2,
            fontWeight: 'bold',
          }}
        >
          {title}
        </Typography>
        <TableContainer
          component={Paper}
          sx={{
            overflowY: 'scroll',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
          }}
        >
          <Table
            sx={{
              color: 'white',
              background: '#1f2c67',
            }}
            stickyHeader
            aria-label={`${title} table`}
          >
            <TableHead
              sx={{
                background: '#1f2c67',
              }}
            >
              <TableRow>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} />
                <TableCell
                  sx={{ color: 'white', fontWeight: 'bold' }}
                  align="right"
                >
                  {t('avslutteSkiftet.in')}
                </TableCell>
                <TableCell
                  sx={{ color: 'white', fontWeight: 'bold' }}
                  align="right"
                >
                  {t('avslutteSkiftet.out')}
                </TableCell>
                <TableCell
                  sx={{ color: 'white', fontWeight: 'bold' }}
                  align="right"
                >
                  {t('avslutteSkiftet.net')}
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {rows.map(({ label, inVal, outVal, netVal }, index) => (
                <TableRow
                  key={label}
                  sx={{
                    '&:last-child td, &:last-child th': { border: 0 },
                    '&:nth-of-type(odd)': {
                      backgroundColor: '#232a45',
                    },
                    '&:nth-of-type(even)': {
                      backgroundColor: '#1a2036',
                    },
                  }}
                >
                  <TableCell sx={{ color: 'white' }}>{label}</TableCell>
                  <TableCell sx={{ color: 'white' }} align="right">
                    {inVal}
                  </TableCell>
                  <TableCell sx={{ color: 'white' }} align="right">
                    {outVal}
                  </TableCell>
                  <TableCell sx={{ color: 'white' }} align="right">
                    {netVal}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Grid>
  );

  /* ----------------------------------------------------------
   * 3 · Render
   * ---------------------------------------------------------- */
  return (
    <Box
      sx={{
        flexGrow: 1,
        p: 2,
      }}
    >
      <Grid container spacing={2}>
        {renderTable(shiftRows, t('avslutteSkiftet.yourTurn'))}
        {renderTable(dayRows, t('avslutteSkiftet.totalToday'))}
      </Grid>
    </Box>
  );
}
